import { useId, useState } from 'react';

import { Button, Dialog, Field, Input } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { loiTheoTruong, maLoi } from '../../../lib/errors';
import { chuanHoaHostname, HOSTNAME_TOI_DA, kiemHostname } from '../nghiep-vu/dia-chi-rieng';
import type { LoiHostname } from '../nghiep-vu/dia-chi-rieng';
import { LoiTrongHop } from './phan-form';

const KHOA_LOI_HOST: Record<LoiHostname, KhoaDich> = {
  trong: 'diaChiRieng.loiTrong',
  dai: 'diaChiRieng.loiDai',
  ip: 'diaChiRieng.loiIp',
  sai: 'diaChiRieng.loiSai',
  'thieu-cham': 'diaChiRieng.loiThieuCham',
};

/**
 * Hộp thêm domain thương hiệu (`POST /api/v1/tenants/{id}/custom-hosts`): Platform Admin thêm hộ khách
 * (`laNenTang`) hoặc Tenant Admin tự thêm (ADR-0097 mục 2); chỉ câu dẫn khác nhau.
 *
 * Người dùng hay dán cả `https://…/client`: hộp chuẩn hoá như server rồi hiện "Sẽ lưu là …" để không có bất ngờ.
 * Domain gốc và subdomain ngang nhau (cách trỏ DNS khác nhau hiện ở khối hướng dẫn sau khi thêm); `www.` là một
 * địa chỉ riêng, tính vào trần `toiDa`. Lỗi của server đặt dưới ô:
 * sai cú pháp, domain của nền tảng, đã đủ 3 domain (`400`, `#/hostname`), hostname đã thuộc tenant khác hoặc còn
 * giữ chỗ (`409 TENANT_DOMAIN_CONFLICT`). Lỗi khác vào dải chung; hộp không đóng khi lỗi.
 *
 * Nơi dùng tăng `key` mỗi lần mở để ô bắt đầu trống, không cần effect.
 */
export function HopThemDiaChi({
  open,
  dangGui,
  loi,
  onClose,
  onThem,
  onSua,
  toiDa,
  laNenTang,
}: {
  /** `max_hosts` của danh sách, để câu gợi ý `www.` nói đúng trần. */
  toiDa: number;
  /** Cổng nền tảng: câu dẫn nói "domain của khách"; cổng tổ chức: "domain của tổ chức". */
  laNenTang: boolean;
  open: boolean;
  dangGui: boolean;
  loi: unknown;
  onClose: () => void;
  onThem: (hostname: string) => void;
  onSua: () => void;
}) {
  const { t } = useT();
  const dichLoi = useLoi();
  const formId = useId();
  const [nhap, setNhap] = useState('');
  const [daGui, setDaGui] = useState(false);

  const host = chuanHoaHostname(nhap);
  const saiCuPhap = kiemHostname(host);
  const loiServerO =
    loiTheoTruong(loi)['/hostname'] ?? (maLoi(loi) === 'TENANT_DOMAIN_CONFLICT' ? dichLoi(loi) : undefined);
  const loiO =
    (saiCuPhap && (daGui || saiCuPhap === 'dai')
      ? t(KHOA_LOI_HOST[saiCuPhap], { so: HOSTNAME_TOI_DA })
      : undefined) ?? loiServerO;
  const loiChung = loi && !loiServerO ? loi : null;
  const daDoi = host !== '' && !saiCuPhap && host !== nhap.trim();

  const gui = () => {
    setDaGui(true);
    if (saiCuPhap || dangGui) return;
    onThem(host);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('diaChiRieng.hopTieuDe')}
      width={560}
      destroyOnHidden
      footer={
        <div className="space-y-3">
          {Boolean(loiChung) && <LoiTrongHop>{dichLoi(loiChung)}</LoiTrongHop>}
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" size="md" onClick={onClose}>
              {t('chung.huy')}
            </Button>
            <Button variant="primary" size="md" htmlType="submit" form={formId} loading={dangGui}>
              {dangGui ? t('diaChiRieng.dangThem') : t('diaChiRieng.them')}
            </Button>
          </div>
        </div>
      }
    >
      <form
        id={formId}
        noValidate
        className="space-y-3 pt-2"
        onSubmit={(e) => {
          e.preventDefault();
          gui();
        }}
      >
        <p className="text-sm leading-relaxed text-content-secondary">
          {t(laNenTang ? 'diaChiRieng.hopThan' : 'diaChiRieng.hopThanToChuc')}
        </p>
        <Field label={t('diaChiRieng.oHostname')} required hint={t('diaChiRieng.oHostnameGoiY')} error={loiO}>
          {(p) => (
            <Input
              {...p}
              status={p['aria-invalid'] ? 'error' : undefined}
              autoFocus
              className="font-mono"
              autoComplete="off"
              spellCheck={false}
              inputMode="url"
              placeholder={t('diaChiRieng.oHostnameViDu')}
              value={nhap}
              onChange={(e) => {
                setNhap(e.target.value);
                if (loi) onSua();
              }}
            />
          )}
        </Field>
        {daDoi && (
          <p className="break-all text-xs text-content-muted">
            {t('diaChiRieng.seLuuLa')} <code className="font-mono text-content-primary">{host}</code>
          </p>
        )}
        <p className="text-xs leading-relaxed text-content-muted">
          {t('diaChiRieng.goiYWww', { host: host && !saiCuPhap && !host.startsWith('www.') ? host : 'congtyabc.com.vn', toiDa })}
        </p>
      </form>
    </Dialog>
  );
}
